import type { EmployeeSummary } from "./employee";

export type DealerType =
  | "INDEPENDENT_DEALERSHIP"
  | "MOBILE_HOME_MILITIA"
  | "PALM_HARBOR_ENTERPRISE"
  | "CAVCO_MANUFACTURED_ENTERPRISE"
  | "SOLITAIRE_HOMES_ENTERPRISE"
  | "TITAN_ENTERPRISE";

export type DealerDocumentType =
  | "WIRE_INFO"
  | "DEALER_PACK"
  | "W9_FORM"
  | "DISCLOSURE_COPIES";

export interface Dealer {
  id: string;
  name: string;
  email: string;
  phone_number: string;
  street1: string;
  street2?: string;
  city: string;
  state: string;
  zip_code: string;
  store_number: string;
  website_url?: string;
  dealer_type: DealerType;
  dealer_feedback?: string;
  lenders: string[];
  onboarding_date?: string;
  last_visit_date?: string;
  uses_dla?: boolean;
  vep_code?: string;
  created_at: string;
  updated_at: string;
  employees: EmployeeSummary[];
  logo_url?: string;
}

export interface DealerSummary {
  id: string;
  name: string;
  email: string;
  phone_number: string;
  store_number: string;
}

export interface DealerDocument {
  document_type: DealerDocumentType;
  file_name: string;
}

export interface UpdateDealerDocument extends DealerDocument {
  content: number[]; // Byte array
}

export interface UpdateDealerDocumentsReq {
  documents: UpdateDealerDocument[];
}

const dealerDocumentLabels: Record<DealerDocumentType, string> = {
  WIRE_INFO: "Wire Information",
  DEALER_PACK: "Dealer Pack",
  W9_FORM: "W-9 Form",
  DISCLOSURE_COPIES: "Disclosure Copies",
};

export const formatDocumentType = (docType: DealerDocumentType): string => {
  return dealerDocumentLabels[docType] || docType;
};

export const getDocumentTypeOptions = () => {
  return Object.entries(dealerDocumentLabels).map(([value, label]) => ({
    value: value as DealerDocumentType,
    label,
  }));
};

const dealerTypeLabels: Record<DealerType, string> = {
  INDEPENDENT_DEALERSHIP: "Independent Dealership",
  MOBILE_HOME_MILITIA: "Mobile Home Militia",
  PALM_HARBOR_ENTERPRISE: "Palm Harbor Enterprise",
  CAVCO_MANUFACTURED_ENTERPRISE: "Cavco Manufactured Enterprise",
  SOLITAIRE_HOMES_ENTERPRISE: "Solitaire Homes Enterprise",
  TITAN_ENTERPRISE: "Titan Enterprise",
};

export const formatDealerType = (dealerType: string | undefined): string => {
  if (!dealerType) return "";

  const upperType = dealerType.toUpperCase() as DealerType;
  return dealerTypeLabels[upperType] || dealerType;
};

export const getDealerTypeOptions = () => {
  return Object.entries(dealerTypeLabels).map(([value, label]) => ({
    value,
    label,
  }));
};
